import { useState, useRef, type FormEvent } from "react";
import { useParams, Link } from "react-router-dom";
import type { ParentInviteImportResponse } from "@dugads/shared";
import * as api from "@/lib/api";
import { Button, buttonVariants } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";

export default function ParentInviteImportPage() {
  const { schoolId } = useParams<{ schoolId: string }>();
  const fileRef = useRef<HTMLInputElement>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ParentInviteImportResponse | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!schoolId) return;

    const file = fileRef.current?.files?.[0];
    if (!file) {
      setError("Please select a CSV file");
      return;
    }

    setError(null);
    setResult(null);
    setSubmitting(true);

    try {
      const res = await api.importParentInvites(schoolId, file);
      setResult(res);
    } catch (err) {
      setError(err instanceof api.ApiError ? err.message : "Import failed");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex flex-col gap-6" data-page="parent-invite-import" data-state={submitting ? "submitting" : error ? "error" : result ? "complete" : "ready"}>
      <div>
        <h1 className="text-2xl font-semibold">Import Parents</h1>
        <p className="text-muted-foreground">
          Upload a CSV with parent contact details. When parents register, they will be automatically linked to their children.
        </p>
      </div>

      <Card className="max-w-lg">
        <CardHeader>
          <CardTitle className="text-base">CSV Upload</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="flex flex-col gap-4" data-region="form" data-form="parent-invite-import">
            {error && <p className="text-sm text-destructive" data-error>{error}</p>}

            <div className="flex flex-col gap-2">
              <Label>Expected Format</Label>
              <pre className="overflow-x-auto rounded bg-muted p-3 text-xs text-muted-foreground">
{`first_name,last_name,email,phone,family_code
Anna,Smith,anna@example.com,0821234567,SMITH01
Maria,Jones,maria@example.com,,JONES02`}
              </pre>
              <p className="text-xs text-muted-foreground">
                Phone is optional (South African format, auto-normalised to +27).
                Family code must match the player's family code for auto-linking to work.
                Siblings share the same code.
              </p>
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="csv-file">CSV File</Label>
              <input
                id="csv-file"
                data-field="csv-file"
                ref={fileRef}
                type="file"
                accept=".csv,text/csv"
                aria-label="CSV File"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm file:border-0 file:bg-transparent file:text-sm file:font-medium"
              />
            </div>

            <div className="flex flex-col gap-2 sm:flex-row">
              <Button type="submit" data-action="submit" disabled={submitting}>
                {submitting ? "Importing..." : "Import"}
              </Button>
              <Link
                to="/parent-invites"
                className={buttonVariants({ variant: "outline" })}
                data-action="cancel"
              >
                Cancel
              </Link>
            </div>
          </form>

          {result && (
            <div className="mt-4 flex flex-col gap-2 rounded border border-border p-4">
              <p className="font-medium">
                {result.inserted} inserted, {result.updated} updated
                {result.skipped > 0 && `, ${result.skipped} skipped`}
              </p>

              {result.warnings.length > 0 && (
                <div className="flex flex-col gap-1">
                  <p className="text-sm font-medium text-amber-700">
                    {result.warnings.length} warning{result.warnings.length !== 1 ? "s" : ""}:
                  </p>
                  <ul className="list-inside list-disc text-sm text-amber-700">
                    {result.warnings.map((w, i) => (
                      <li key={i}>Row {w.row}: {w.message}</li>
                    ))}
                  </ul>
                </div>
              )}

              {result.errors.length > 0 && (
                <div className="flex flex-col gap-1">
                  <p className="text-sm font-medium text-destructive">
                    {result.errors.length} error{result.errors.length !== 1 ? "s" : ""}:
                  </p>
                  <ul className="list-inside list-disc text-sm text-destructive">
                    {result.errors.map((err, i) => (
                      <li key={i}>Row {err.row}: {err.message}</li>
                    ))}
                  </ul>
                </div>
              )}

              {result.errors.length === 0 && (
                <Link
                  to="/parent-invites"
                  className={buttonVariants({ variant: "outline", size: "sm" })}
                >
                  View Parent Invites
                </Link>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
